import { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import Select, { createFilter } from 'react-select';
import OptionsType from 'react-select';
import { toast } from 'react-toastify';
import { AssetSvg } from '../../assets';
import { FormErrorMessage } from '../../components';
import {
  InputFieldValidationErrorMessage,
  PlatformType,
  reactSelectFilterConfig,
} from '../../configs';
import { useAppSelector, VRIAppStateType } from '../../core/Redux/ReduxStore';
import {
  ApiSchemaCreateLanguagePack,
  ApiSchemaLanguagePackData,
  Option,
  TypeLanguageList,
} from '../../models';
import { useFunctionalityLanguage, useFunctionalityLanguagePack } from '../../pages/hooks';
import { useFunctionalityInputChecker } from '../../pages/hooks/functionalityInputChecker';
import { ModalFooter } from '../Common';

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  dataToEdit: ApiSchemaLanguagePackData;
  companyId: string;
  onUpdateSuccess?: boolean;
  setOnUpdateSuccess: any;
  selectedPlatformType?: number;
  isCopyLanguagePack?: boolean;
  onCopyLanguagePackHandler?: any;
};

type SeedMode = 'MAX' | 'FLAT' | 'ZERO' | 'CUSTOM';

export function ModalLanguagePackV2(props: Props): JSX.Element {
  const {
    show,
    handleShow,
    dataToEdit,
    companyId,
    selectedPlatformType,
    isCopyLanguagePack,
    onUpdateSuccess,
    setOnUpdateSuccess,
    onCopyLanguagePackHandler
  } = props;
  const { register, handleSubmit, formState: { errors }, watch, setValue, trigger, clearErrors } =
    useForm<ApiSchemaCreateLanguagePack>({
      shouldUnregister: false,
      defaultValues: {
        name: dataToEdit?.name ?? "",
        flatRate: Number(dataToEdit?.flatRate ?? 0),
        languageRates: [],
      },
    });
  const vriCompanyId =
    useAppSelector((state: VRIAppStateType) => state.auth.profile?.companyId) ?? '';

  let tempOption: Option[] = [];
  let tempLanguageList: TypeLanguageList[] = [];
  const [selectedOptions, setSelectedOptions] = useState<Option[]>(tempOption);
  const [listOfLanguages, setListOfLanguages] = useState(tempLanguageList);
  const [flatRateToggle, setFlatRateToggle] = useState<boolean>(false);
  const [languageFlatRateError, setLanguageFlatRateError] = useState<string>('');
  const [zeroRateState, setZeroRateState] = useState<any>([]);
  const [disableProp, setDisableProp] = useState<boolean>(false);
  const [seedMode, setSeedMode] = useState<SeedMode>('FLAT');
  const [seedCustom, setSeedCustom] = useState<number>(0);
  const [showApplyAllConfirm, setShowApplyAllConfirm] = useState(false);

  let { languageList } = useFunctionalityLanguage({
    plaformType: PlatformType.All,
    shouldLanguageGetAllInvoke: show,
  });
  const { onLanguagePackNameIsExists, onSubmit } = useFunctionalityLanguagePack({ companyId: companyId, });
  const lastIndividualRatesRef = useRef<Map<string, number>>(new Map());
  const preFlatIdsRef = useRef<Set<string>>(new Set());


  if (selectedPlatformType === PlatformType.OPI) {
    languageList = languageList.filter((language) => language.label !== 'ASL');
  }
  const languageListForSelect = [
    { label: 'Select All', value: 'all' },
    ...languageList,
  ];
  const {
    numberFieldValidation,
    handleCopyPasteInvalidValue,
    handleLimitDecimalPlaces,
    useFunctionlityInputFieldValidation,
  } = useFunctionalityInputChecker();

  const validatePaste = (v: any) =>
    handleCopyPasteInvalidValue(typeof v === 'number' ? String(v) : (v ?? ''));

  useEffect(() => {
    if (dataToEdit?.languageRates) {
      tempOption = dataToEdit?.languageRates.map((t) => {
        return {
          value: t.languageId,
          label: t.languageName,
        };
      });
      tempLanguageList = dataToEdit?.languageRates.map((option) => {
        return {
          rate: option?.rate ? option.rate : 0.0,
          languageId: option.languageId,
          languageName: option.languageName,
        };
      });
      let initialRates: any = [];
      dataToEdit?.languageRates?.forEach((option, index) => {
        initialRates = [
          ...initialRates,
          {
            name: `languageRates[${index}].rate`,
            value: option?.rate,
          },
        ];
      });
      setZeroRateState(initialRates);
    }

    dataToEdit && dataToEdit.flatRate > 0
      ? setFlatRateToggle(true)
      : setFlatRateToggle(false);

    setSelectedOptions(tempOption);
    setListOfLanguages(tempLanguageList);
  }, [dataToEdit]);

  const doCopyUpdateOrAddLanguagePack = (form: ApiSchemaCreateLanguagePack) => {
    const isEmptyObject = Object.keys(dataToEdit).length === 0 && dataToEdit.constructor === Object;
    if (isEmptyObject) {
      onLanguagePackNameIsExists(vriCompanyId, form?.name)?.then((res) => {
        if (res) {
          toast.error("Language pack already exists");
          handleShow(false);
          return;
        }
        onSubmit(form).then((res) => {
          setOnUpdateSuccess?.(!onUpdateSuccess);
          handleShow(false);
        });
      }).catch((error) => {
        console.error(error);
      });
    } else {
      if (isCopyLanguagePack) {
        onLanguagePackNameIsExists(vriCompanyId, form?.name)?.then((res) => {
          if (res) {
            toast.error("Language pack already exists");
            handleShow(false);
            return;
          }
          onSubmit(form).then((res) => {
            setOnUpdateSuccess?.(!onUpdateSuccess);
            handleShow(false);
            onCopyLanguagePackHandler?.(res?.id ?? "", dataToEdit?.id);
          });
        }).catch((error) => {
          console.error(error);
        });
      } else {
        if (form?.name !== dataToEdit?.name) {
          onLanguagePackNameIsExists(vriCompanyId, form?.name, dataToEdit.id)?.then((res) => {
            if (res) {
              toast.error("Language pack already exists");
              handleShow(false);
              return;
            }
            onSubmit(form, dataToEdit.id).then(() => {
              handleShow(false);
              setOnUpdateSuccess?.(!onUpdateSuccess);
            });
          }).catch((error) => {
            console.error(error);
          });
        } else {
          onSubmit(form, dataToEdit.id).then(() => {
            handleShow(false);
            setOnUpdateSuccess?.(!onUpdateSuccess);
          });
        }
      }
    }
  };

  const onSubmitHandler = (form: ApiSchemaCreateLanguagePack) => {
    const rawFlat = Number(form.flatRate);
    const flatEnabled = flatRateToggle && Number.isFinite(rawFlat) && rawFlat > 0;
    const normalizedFlat = flatEnabled ? rawFlat : 0;

    form = {
      ...form,
      flatRate: normalizedFlat,
      IsFlatRateEnabled: flatEnabled,
      name: form.name.trim(),
      languageRates: listOfLanguages.map(t => ({
        languageId: t.languageId,
        languageName: t.languageName,
        rate: Number(t.rate ?? 0),
      })),
      vendorCompanyId: isCopyLanguagePack ? vriCompanyId : companyId,
    };

    doCopyUpdateOrAddLanguagePack(form);
  };


  const doOperation = (dataToEdit: any, isCopyLanguagePack?: boolean) => {
    if (dataToEdit && Object.keys(dataToEdit).length !== 0) {
      return isCopyLanguagePack ? 'Copy' : 'Update';
    } else {
      return 'Add';
    }
  };


  const handleApplyAllConfirmed = () => {
    applyPresetToAll();
    setShowApplyAllConfirm(false);
  };

  // @ts-ignore
  const handleAddLanguage = (selected: OptionsType<Option>) => {
    const selectedAll = selected.length && selected.some((o: any) => o.value === 'all');
    const selectedOptionsAny = selectedAll ? languageListForSelect.slice(1) : selected;

    setSelectedOptions(selectedOptionsAny as any);

    const seed = getSeedValue();

    const nextList: TypeLanguageList[] = (selectedOptionsAny as any).map((option: any) => {
      const existing = listOfLanguages.find(l => l.languageId === option.value);
      const existingRate = Number(existing?.rate ?? 0);

      const lastTyped = lastIndividualRatesRef.current.get(option.value);
      const lastTypedNum = Number(lastTyped ?? 0);

      const finalRate =
        existingRate > 0 ? existingRate :
          lastTypedNum > 0 ? lastTypedNum :
            seed;

      return {
        languageId: option.value,
        languageName: option.label,
        rate: finalRate,
      };
    });

    setListOfLanguages(nextList);

    nextList.forEach((l, i) => {
      setValue(`languageRates.${i}.languageId` as const, l.languageId);
      setValue(`languageRates.${i}.languageName` as const, l.languageName);
      setValue(`languageRates.${i}.rate` as const, Number(l.rate ?? 0), { shouldDirty: true, shouldValidate: true });
    });
    trigger('languageRates');

    setZeroRateState(nextList.map((l, index) => ({
      name: `languageRates[${index}].rate`,
      value: l.rate ?? 0,
    })));
  };


  const handleFlatRateButtonToggle = (previousToggle: boolean) => {
    const nextToggle = !flatRateToggle;
    setFlatRateToggle(nextToggle);

    if (!previousToggle && nextToggle) {
      preFlatIdsRef.current = new Set(listOfLanguages.map(l => l.languageId));

      const snapshot = new Map<string, number>();
      listOfLanguages.forEach(l => {
        snapshot.set(l.languageId, Number(l.rate ?? 0));
      });
      lastIndividualRatesRef.current = snapshot;

      clearErrors('languageRates');
      const flatNow = getFlat();
      setValue('flatRate' as const, Number(flatNow), { shouldDirty: true, shouldValidate: true });

      if (flatRateToggle && flatNow <= 0) {
        setDisableProp(true);
        setLanguageFlatRateError('flat rate must be greater than zero');
      } else {
        setDisableProp(false);
        setLanguageFlatRateError('');
        clearErrors('flatRate');
      }
      trigger('flatRate');
    }

    if (previousToggle && !nextToggle) {
      const seed = getSeedValue();

      const snapshot = lastIndividualRatesRef.current;
      const preIds = preFlatIdsRef.current;

      const restored = listOfLanguages.map(l => {
        if (preIds.has(l.languageId)) {
          const backed = snapshot.get(l.languageId);
          const restoredVal = Number.isFinite(backed!) ? Number(backed) : 0;
          return { ...l, rate: restoredVal };
        } else {
          const current = Number(l.rate ?? 0);
          return { ...l, rate: current > 0 ? current : seed };
        }
      });

      setListOfLanguages(restored);

      restored.forEach((l, i) => {
        setValue(`languageRates.${i}.languageId` as const, l.languageId);
        setValue(`languageRates.${i}.languageName` as const, l.languageName);
        setValue(`languageRates.${i}.rate` as const, Number(l.rate ?? 0), { shouldDirty: true, shouldValidate: true });
      });

      setValue('flatRate', 0, { shouldDirty: true, shouldValidate: true });
      clearErrors('flatRate');
      trigger('languageRates');

      setZeroRateState(
        restored.map((l, i) => ({ name: `languageRates[${i}].rate`, value: l.rate ?? 0 }))
      );
    }


    if ((!previousToggle && (dataToEdit?.flatRate ?? 0) <= 0) && getFlat() <= 0) {
      setDisableProp(true);
      setLanguageFlatRateError('flat rate must be greater than zero');
    } else if (nextToggle && getFlat() > 0) {
      setDisableProp(false);
      setLanguageFlatRateError('');
    }
  };

  const applyPresetToZeros = () => {
    const seed = getSeedValue();
    if (seed <= 0) return;

    const patched = listOfLanguages.map(l => {
      const current = Number(l.rate ?? 0);
      if (current > 0) return l;
      return { ...l, rate: seed };
    });

    setListOfLanguages(patched);

    patched.forEach((l, i) => {
      setValue(`languageRates.${i}.rate` as const, Number(l.rate ?? 0), { shouldDirty: true, shouldValidate: true });
    });
    trigger('languageRates');

    setZeroRateState(patched.map((l, i) => ({
      name: `languageRates[${i}].rate`,
      value: l.rate ?? 0,
    })));
  };

  const applyPresetToAll = () => {
    const seed = getSeedValue();
    if (seed <= 0) return;

    const patched = listOfLanguages.map(l => ({
      ...l,
      rate: seed,
    }));

    setListOfLanguages(patched);

    patched.forEach((l, i) => {
      setValue(
        `languageRates.${i}.rate` as const,
        Number(l.rate ?? 0),
        { shouldDirty: true, shouldValidate: true }
      );
    });
    trigger('languageRates');

    setZeroRateState(
      patched.map((l, i) => ({
        name: `languageRates[${i}].rate`,
        value: l.rate ?? 0,
      }))
    );
  };



  const handleFlatRate = (event: any) => {
    const raw = event.target.value;
    const value = Number(raw);

    setValue('flatRate' as const, value, { shouldDirty: true, shouldValidate: true });

    if (!raw || value <= 0) {
      setDisableProp(true);
      setLanguageFlatRateError('flat rate must be greater than zero');
    } else {
      setDisableProp(false);
      setLanguageFlatRateError('');
      clearErrors('flatRate');
      trigger('flatRate');
    }
  };


  const handleRateChange = (event: any, language: TypeLanguageList) => {
    const name = event.target.name;
    const value = Number(event.target.value);

    const changeRate = listOfLanguages.map((item) =>
      item.languageId === language.languageId ? { ...item, rate: value } : item
    );
    setListOfLanguages(changeRate);

    const newState = [
      ...zeroRateState.filter((zr: any) => zr?.name !== name),
      { name, value },
    ];
    setZeroRateState(newState);
    setValue(name as any, value, { shouldDirty: true, shouldValidate: true });
    trigger(name as any);
    lastIndividualRatesRef.current.set(language.languageId, value);
  };

  const onRemoveItem = (id: string) => {
    const remainingItem = selectedOptions.filter((item) => item.value !== id);
    setSelectedOptions(remainingItem);
    const listAfterRemove = listOfLanguages?.filter(
      (language) => language?.languageId !== id
    );
    setListOfLanguages(listAfterRemove);
    let rateStateAfterRemoveLanguage: any = [];
    listAfterRemove?.forEach((option, index) => {
      rateStateAfterRemoveLanguage = [
        ...rateStateAfterRemoveLanguage,
        {
          name: `languageRates[${index}].rate`,
          value: option?.rate,
        },
      ];
    });

    setZeroRateState(rateStateAfterRemoveLanguage);
    lastIndividualRatesRef.current.delete(id);
  };

  const getFlat = () => {
    const watched = Number(watch('flatRate'));
    if (Number.isFinite(watched) && watched > 0) return watched;
    const saved = Number(dataToEdit?.flatRate ?? 0);
    return saved > 0 ? saved : 0;
  };

  const maxIndividual = () => {
    const nums = listOfLanguages
      .map(l => Number(l.rate ?? 0))
      .filter(n => Number.isFinite(n) && n > 0);
    return nums.length ? Math.max(...nums) : 0;
  };

  const getSeedValue = () => {
    switch (seedMode) {
      case 'FLAT': {
        const flat = getFlat();
        return flat > 0 ? flat : 0;
      }
      case 'MAX':
        return maxIndividual();
      case 'CUSTOM':
        return Number(seedCustom) > 0 ? Number(seedCustom) : 0;
      case 'ZERO':
      default:
        return 0;
    }
  };


  const computeFallbackRate = (basis: TypeLanguageList[]) => {
    const flat = getFlat();
    if (flat > 0) return flat;

    const nums = basis
      .map(l => Number(l.rate ?? 0))
      .filter(n => Number.isFinite(n));

    return nums.length ? Math.max(...nums) : 0;
  };


  useEffect(() => {
    const hasZeroRate = zeroRateState?.find((zero: any) => zero?.value < 0.01);
    if (hasZeroRate && zeroRateState?.length > 0 && !flatRateToggle) {
      setDisableProp(true);
    } else if (flatRateToggle && languageFlatRateError) {
      setDisableProp(true);
    } else {
      setDisableProp(false);
    }
  }, [zeroRateState, flatRateToggle]);

  return (
    <>
      <Modal show={show} onHide={() => handleShow(false)} centered>
        <form onSubmit={handleSubmit(onSubmitHandler)}>
          {/*@ts-ignore */}
          <Modal.Header closeButton>
            <Modal.Title>
              {doOperation(dataToEdit, isCopyLanguagePack)} Language Pack{' '}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-12">
                <div className="form-group">
                  <label
                    htmlFor=""
                    className="position-relative required font-sz-14"
                  >
                    Package Name
                  </label>
                  <input
                    type="text"
                    {...register('name', {
                      required: true,
                      validate: useFunctionlityInputFieldValidation,
                    })}
                    className="form-control"
                    placeholder="Package name"
                    defaultValue={dataToEdit?.name} />
                  {errors.name && errors.name.type == 'validate' && (
                    <FormErrorMessage
                      message={InputFieldValidationErrorMessage.ErrorMessage}
                    />
                  )}
                  {errors.name?.type === 'required' && (
                    <FormErrorMessage message="required" />
                  )}
                </div>
                <div className="form-inline mb-3 flex-wrap align-items-sm-center">
                  <div className="form-group col-12 pl-0 pr-0 ">
                    <Select
                      placeholder="Select Language"
                      className="w-100"
                      options={languageListForSelect}
                      value={selectedOptions}
                      isMulti
                      isDisabled={languageList.length === 0 ? true : false}
                      closeMenuOnSelect={false}
                      filterOption={createFilter(reactSelectFilterConfig)}
                      components={{
                        IndicatorSeparator: () => null,
                      }}
                      onChange={(selected) => handleAddLanguage(selected)}
                    />
                  </div>
                </div>
                <div className="component-card mb-3 shadow-none">
                  <div className="component-card-header border-top border-right border-left">
                    <div className="row align-items-start">
                      <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                        <div>Language</div>
                        {listOfLanguages && listOfLanguages.length === 0 ? (
                          <div className="text-danger font-sz-12 ">
                            Contains no language
                          </div>
                        ) : (
                          <div className="text-muted font-sz-12 ">
                            Total {listOfLanguages.length} Selected Languages
                          </div>
                        )}
                      </div>
                      <div className="col">
                        <div className="row  flex-wrap  align-items-start mx-0">
                          <button
                            type="button"
                            className={`btn btn-toggle language-toggle-btn mt-1 ${dataToEdit.flatRate != undefined &&
                              dataToEdit.flatRate > 0
                              ? 'active'
                              : ''
                              }`}
                            data-toggle="button"
                            aria-pressed="false"
                            onClick={() =>
                              handleFlatRateButtonToggle(flatRateToggle)
                            }
                          >
                            <div className="handle"></div>
                          </button>
                          <span className="toggle-button-label  text-muted ">
                            Flat Language Rate
                          </span>
                          {flatRateToggle ? (
                            <>
                              <div className="input-group col pr-0">
                                <div className="input-group-prepend">
                                  <span
                                    className="input-group-text"
                                    id="flat-rate"
                                  >
                                    {' '}
                                    ${' '}
                                  </span>
                                </div>
                                <input
                                  type="number"
                                  step="any"
                                  min="0"
                                  {...register('flatRate', {
                                    valueAsNumber: true,
                                    validate: validatePaste,
                                  })}
                                  className={`form-control ${languageFlatRateError ? 'field-required' : ''
                                    }`}
                                  defaultValue={dataToEdit.flatRate ?? 0}
                                  aria-describedby="flatRate"
                                  style={{
                                    border:
                                      errors.flatRate || languageFlatRateError
                                        ? '1px solid red'
                                        : '',
                                  }}
                                  onKeyDown={(evt) => numberFieldValidation(evt)}
                                  onKeyPress={(event) => {
                                    if (!/[0-9]|\./.test(event.key)) {
                                      event.preventDefault();
                                    }
                                  }}
                                  onChange={(event) => {
                                    handleFlatRate(event);
                                  }}
                                  onInput={(event) =>
                                    handleLimitDecimalPlaces(event, 2)
                                  } />
                                {errors.flatRate &&
                                  errors.flatRate.type === 'validate' && (
                                    <FormErrorMessage message="Please enter a valid value" />
                                  )}
                                {languageFlatRateError && (
                                  <div className="font-sz-10 text-danger">
                                    {languageFlatRateError}
                                  </div>
                                )}
                              </div>
                            </>
                          ) : (
                            <></>

                          )}
                        </div>
                      </div>
                    </div>

                    {!flatRateToggle ? (
                      <div>
                        <div className="d-flex flex-wrap gap-2 mt-2 align-items-center">
                          <label className="mr-2 mb-1">Default for new langs:</label>
                          <select
                            className="form-control w-auto"
                            value={seedMode}
                            onChange={(e) => setSeedMode(e.target.value as SeedMode)}
                          >
                            <option value="FLAT">Use flat rate</option>
                            <option value="MAX">Use max of existing</option>
                            <option value="ZERO">Use zero</option>
                            <option value="CUSTOM">Custom preset…</option>
                          </select>

                          {seedMode === 'CUSTOM' && (
                            <input
                              type="number"
                              min="0"
                              step="any"
                              className="form-control w-auto ml-2"
                              placeholder="Custom value"
                              value={Number(seedCustom) || ''}
                              onChange={(e) => setSeedCustom(Number(e.target.value))}
                            />
                          )}
                        </div>
                        <div className="w-100 d-flex flex-wrap align-items-end justify-content-between mt-4">

                          <div className="text-muted d-flex align-items-center">
                            New languages will start at: <strong className="ml-1" style={{ color: "var(--site-purple)" }}>{getSeedValue() || 0}</strong>
                          </div>

                          <div>
                            <button
                              type="button"
                              className="btn btn-outline-secondary mr-2"
                              onClick={applyPresetToZeros}
                              title="Set the preset only on languages that are currently 0"
                            >
                              Apply to current 0’s
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => setShowApplyAllConfirm((prev) => !prev)}
                              title="Set the preset to all languages"
                            >
                              Apply to all
                            </button>
                          </div>
                        </div>

                        <div
                          className={`apply-all-confirm-wrapper ${showApplyAllConfirm ? 'is-open' : ''
                            }`}
                        >
                          <div className="apply-all-confirm-inner alert alert-warning d-flex flex-column flex-sm-row align-items-sm-center justify-content-between">
                            <div className="mb-2 mb-sm-0">
                              <strong>Are you sure?</strong>{' '}
                              <p className="mb-0">
                              This will overwrite the rate of all selected languages to{' '}
                              <strong>${getSeedValue() || 0}</strong>.
                              </p>
                            </div>

                            <div className="d-flex">
                              <button
                                type="button"
                                className="btn btn-sm btn-danger mr-2"
                                onClick={handleApplyAllConfirmed}
                                disabled={getSeedValue() <= 0}
                              >
                                Yes
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setShowApplyAllConfirm(false)}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>

                      </div>
                    ) : null}
                  </div>

                  <div className="component-card-body border">
                    <ul className="list-group">
                      {listOfLanguages &&
                        listOfLanguages.map(
                          (language: TypeLanguageList, index) => (
                            <li
                              className="list-group-item"
                              key={language.languageId}
                            >
                              <div className="row">
                                <div className="col-12 col-sm-6 col-md-6">
                                  <p className="word-wrap mb-sm-0 mb-2 pt-2">
                                    {language.languageName}
                                  </p>
                                </div>
                                <div className="col-12 col-sm-5 col-md-5 pr-sm-0 mb-2 mb-sm-0">
                                  {flatRateToggle ? null : (
                                    <div className="input-group">
                                      <div className="input-group-prepend">
                                        <span
                                          className="input-group-text"
                                          id="flat-rate"
                                        >
                                          $
                                        </span>
                                      </div>

                                      <input
                                        type="hidden"
                                        className="form-control"
                                        //@ts-ignore
                                        {...register(`languageRates[${index}].languageName`, {
                                          required: flatRateToggle ? true : false,
                                        })}
                                        aria-describedby="languageName"
                                        value={language.languageName}
                                      />

                                      <input
                                        type="hidden"
                                        className="form-control"
                                        //@ts-ignore
                                        {...register(`languageRates[${index}].languageId`, {
                                          required: flatRateToggle ? true : false,
                                        })}
                                        aria-describedby="languageId"
                                        value={language.languageId}
                                      />

                                      <input
                                        type="number"
                                        min="0"
                                        step="any"
                                        //@ts-ignore
                                        {...register(`languageRates[${index}].rate`, {
                                          required: true,
                                          validate: validatePaste,
                                        })}
                                        aria-describedby="rate"
                                        className="form-control"
                                        value={Number(language.rate ?? 0)}
                                        style={{ border: !language.rate ? '1px solid red' : '' }}
                                        onKeyDown={(evt) => numberFieldValidation(evt)}
                                        onKeyPress={(event) => { if (!/[0-9]|\./.test(event.key)) event.preventDefault(); }}
                                        onChange={(event) => { handleRateChange(event, language); }}
                                        onInput={(event) => handleLimitDecimalPlaces(event, 2)}
                                      />


                                      {!language.rate && (
                                        <span className="font-sz-14 text-danger">
                                          must be greater than 0
                                        </span>
                                      )}


                                      {
                                        errors.languageRates?.[index]?.rate ? (
                                          (errors.languageRates?.[index]?.rate?.type === "validate") ? (
                                            <FormErrorMessage message="Please enter a valid value" />
                                          ) : null
                                        ) : null
                                      }
                                    </div>
                                  )}
                                </div>
                                <div className="col-12 col-sm-1 col-md-1 px-sm-2 text-left text-sm-center ">
                                  <button
                                    type="button"
                                    className="btn btn-link px-0 text-site"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      onRemoveItem(language.languageId);
                                    }}
                                  >
                                    <span>
                                      <AssetSvg.Delete />
                                    </span>
                                  </button>
                                </div>
                              </div>
                            </li>
                          )
                        )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <ModalFooter
              handleShow={handleShow}
              operationName={doOperation(dataToEdit, isCopyLanguagePack)}
              zeroRateLanguagePack={disableProp}
            />
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
}
